import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Button } from 'primereact/button';
import { Menu } from 'primereact/menu';
import { Toast } from 'primereact/toast';

import { TreeNode } from '../../types/interfaces';
import TablePageHeader from '../../ui/shared/TablePageHeader';

import { useCalendarAssignmentsMobileActions } from './CalendarAssignmentsMobileActions';
import { EntityVisibility } from './EntityVisibility';
import VisibilityControlToggle from './VisibilityControlToggle';

export interface CalendarAssignmentsHeaderProps {
  selectedDepartmentFilter: TreeNode | null;
  treeDropDownValue: TreeNode[];
  handleSelectionChange: (selection: TreeNode) => void;
  handleDateFilterChange: (selection: Date) => void;
  datepickerDate?: Date | null;
  isDepartmentsLoaded: boolean;
  toast: React.MutableRefObject<Toast | null>;
  hasUnsavedChanges: boolean;
  onSaveChanges: () => void;
  onAdd: (actionType: 'shift' | 'absence' | 'task') => void;
  onRecalculateClick: () => void;
  onDeleteShiftsClick: () => void;
  onDeleteAbsencesClick: () => void;
  visibleEntities: EntityVisibility;
  handleApplyVisibility: (entities: EntityVisibility) => void;
  viewMode: 'schedule' | 'timesheet';
  onToggleViewMode: () => void;
  onToggleExportDialogVisible: () => void;
  exportTemplate: () => void;
  importAbsencesXLS: (e: React.ChangeEvent<HTMLInputElement>) => void;
  hasHorizontalScroll: boolean;
  unassignedTasksCount: number;
  onOpenUnassignedTasks: () => void;
}

const CalendarAssignmentsHeader: React.FC<CalendarAssignmentsHeaderProps> = ({
  selectedDepartmentFilter,
  treeDropDownValue,
  handleSelectionChange,
  handleDateFilterChange,
  datepickerDate,
  toast,
  hasUnsavedChanges,
  onSaveChanges,
  onAdd,
  onRecalculateClick,
  onDeleteShiftsClick,
  onDeleteAbsencesClick,
  visibleEntities,
  handleApplyVisibility,
  viewMode,
  onToggleViewMode,
  onToggleExportDialogVisible,
  exportTemplate,
  importAbsencesXLS,
  hasHorizontalScroll,
  unassignedTasksCount,
  onOpenUnassignedTasks
}) => {
  const addMenuRef = useRef<Menu>(null);
  const deleteMenuRef = useRef<Menu>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false
  );

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const addMenuItems = [
    {
      label: 'Создать смену',
      icon: 'pi pi-plus-circle',
      command: () => onAdd('shift')
    },
    {
      label: 'Создать отсутствие',
      icon: 'pi pi-plus-circle',
      command: () => onAdd('absence')
    },
    {
      label: 'Создать наряд',
      icon: 'pi pi-plus-circle',
      className: 'tasks-add-btn__roles whole wholeui__roles',
      command: () => onAdd('task')
    }
    // будущие опции
  ];

  const deleteMenuItems = [
    {
      label: 'Удаление смен',
      icon: 'pi pi-clock',
      command: () => onDeleteShiftsClick()
    },
    {
      label: 'Удаление отсутствий',
      icon: 'pi pi-calendar-times',
      command: () => onDeleteAbsencesClick() // Новая функция
    }
  ];

  const departmentIsSelected = !!selectedDepartmentFilter?.key;

  const onHistoryClick = useCallback(() => {
    if (!selectedDepartmentFilter?.key) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Предупреждение',
        detail: 'Выберите участок для просмотра истории',
        life: 3000
      });
      return;
    }

    // Формируем параметры
    const params = new URLSearchParams();
    params.append('departmentIds', selectedDepartmentFilter.key.toString());

    if (datepickerDate) {
      const month = datepickerDate.getMonth() + 1;
      const year = datepickerDate.getFullYear();
      params.append('month', month.toString());
      params.append('year', year.toString());
    }

    window.open(`/history?${params.toString()}`, '_blank');
  }, [selectedDepartmentFilter, datepickerDate, toast]);

  const customAddButton = [
    hasUnsavedChanges && (
      <Button
        key="save-changes"
        icon="pi pi-save"
        className="calendarAssignments-save-btn__roles p-button-success mr-2"
        onClick={onSaveChanges}
        rounded
        label="Сохранить изменения"
      />
    ),
    departmentIsSelected && (
      <Button
        key="add-shift"
        className="calendarAssignments-add-btn__roles calendar-plus-btn"
        type="button"
        icon="pi pi-plus"
        severity="success"
        rounded
        label="Добавить"
        onClick={(e) => addMenuRef.current?.toggle(e)}
      />
    ),
    <Menu key="add-menu" model={addMenuItems} popup ref={addMenuRef} />
  ].filter(Boolean);

  const importButtons = [
    <input
      key="input_file"
      type="file"
      accept="csv"
      onChange={importAbsencesXLS}
      style={{ visibility: 'hidden', width: '0px' }}
      ref={fileInputRef}
    />,
    <Button
      key={`absencePersons-exportTemplate-btn-${hasHorizontalScroll}`}
      className="absencePersons-exportTemplate-btn__roles"
      type="button"
      severity="secondary"
      icon="pi pi-file-excel"
      rounded
      onClick={exportTemplate}
      tooltip="Выгрузка актуального шаблона импорта"
      tooltipOptions={{ position: hasHorizontalScroll ? 'left' : 'top' }}
    />,
    <Button
      key="absencePersons-import-btn"
      className="absencePersons-import-btn__roles"
      type="button"
      severity="secondary"
      icon="pi pi-file-import"
      rounded
      onClick={() => {
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        fileInputRef.current?.click();
      }}
      label="Импорт"
    />
  ];

  const mobileActions = useCalendarAssignmentsMobileActions({
    selectedDepartmentFilter,
    departmentIsSelected,
    addMenuItems,
    deleteMenuItems,
    hasUnsavedChanges,
    onSaveChanges,
    onRecalculateClick,
    visibleEntities,
    handleApplyVisibility,
    viewMode,
    onToggleViewMode,
    onToggleExportDialogVisible,
    onHistoryClick,
    exportTemplate,
    importAbsencesXLS,
    hasHorizontalScroll,
    unassignedTasksCount,
    onOpenUnassignedTasks,
    isMobile
  });

  return (
    <TablePageHeader
      tableName="tasks"
      globalSearch={false}
      onCalendarChange={handleDateFilterChange}
      singleCalendarMode
      datepicker
      datepickerDate={datepickerDate}
      treeDropdowns={[
        {
          key: 'departments_custom',
          mode: 'custom',
          dropdownProps: {
            assetOptions: treeDropDownValue,
            placeholder: 'Выбор участка',
            selectedAsset: selectedDepartmentFilter,
            setSelectedAsset: (val) => handleSelectionChange(val as TreeNode),
            onTreeSelectionChange: (val) =>
              handleSelectionChange(val as TreeNode),
            useTree: true,
            treeValue: treeDropDownValue,
            multiplySelectMode: false,
            overlayWidthMultiplier: 2,
            allowDeselect: true
          }
        }
      ]}
      customButtonsLeft={
        isMobile
          ? mobileActions.buttonsLeft
          : [
              <Button
                key="recalculate-schedules"
                icon="pi pi-calculator"
                className="calendarAssignments-calc-btn__roles p-button-rounded p-button-outlined ml-2"
                tooltip="Рассчитать плановый график на месяц"
                onClick={onRecalculateClick}
                disabled={!selectedDepartmentFilter || viewMode === 'timesheet'}
                tooltipOptions={{
                  position: hasHorizontalScroll ? 'left' : 'top'
                }}
              />,
              departmentIsSelected && (
                <Button
                  key="unassigned-tasks"
                  icon="pi pi-list"
                  severity="secondary"
                  className="calendarAssignments-unassigned-btn p-button-rounded p-button-outlined ml-2"
                  tooltip="Неназначенные наряды"
                  tooltipOptions={{
                    position: hasHorizontalScroll ? 'left' : 'top'
                  }}
                  onClick={onOpenUnassignedTasks}
                  badge={String(unassignedTasksCount ?? 0)}
                  badgeClassName={
                    (unassignedTasksCount ?? 0) > 0
                      ? 'unassignedTasks-badge unassignedTasks-badge--danger'
                      : 'unassignedTasks-badge unassignedTasks-badge--success'
                  }
                  label="Неназначенные"
                />
              ),
              <VisibilityControlToggle
                key="visibility-toggle"
                visibleEntities={visibleEntities}
                onApply={handleApplyVisibility}
              />,
              <Button
                key="calendarAssignments-logs"
                icon="pi pi-history"
                severity="secondary"
                tooltip="История изменения графика"
                className="p-button-rounded p-button-outlined"
                onClick={onHistoryClick}
                tooltipOptions={{
                  position: hasHorizontalScroll ? 'left' : 'top'
                }}
              />,
              <Button
                key="view-mode-toggle"
                icon={
                  viewMode === 'schedule' ? 'pi pi-table' : 'pi pi-calendar'
                }
                severity={viewMode === 'timesheet' ? 'warning' : 'secondary'}
                tooltip={
                  viewMode === 'schedule'
                    ? 'Просмотр табеля'
                    : 'Просмотр графика'
                }
                className={`p-button-rounded ${
                  viewMode === 'schedule' ? 'p-button-outlined' : ''
                } ml-2`}
                onClick={onToggleViewMode}
                tooltipOptions={{
                  position: hasHorizontalScroll ? 'left' : 'top'
                }}
              />,
              <Button
                key="calendarAssignments-export"
                icon="pi pi-download"
                severity="secondary"
                tooltip={
                  viewMode === 'schedule' ? 'Экспорт графика' : 'Экспорт табеля'
                }
                className="calendarAssignments-export-btn__roles p-button-rounded p-button-outlined ml-2"
                onClick={onToggleExportDialogVisible}
                tooltipOptions={{
                  position: hasHorizontalScroll ? 'left' : 'top'
                }}
              />,
              viewMode === 'timesheet' && (
                <p
                  key="1"
                  style={{
                    fontSize: '12px',
                    fontWeight: 'normal',
                    display: 'flex',
                    alignItems: 'center',
                    marginLeft: '15px',
                    background: '#fff2e2',
                    color: '#cc8925',
                    padding: '6px',
                    borderRadius: 5
                  }}
                >
                  Страница открыта в режиме просмотра табеля. <br />
                  Корректировка данных недоступна в данном виде отображения
                </p>
              )
            ]
      }
      customButtonsRight={
        isMobile
          ? mobileActions.buttonsRight
          : [
              viewMode === 'schedule' && importButtons,
              viewMode === 'schedule' && customAddButton,
              viewMode === 'schedule' && departmentIsSelected && (
                <React.Fragment key="delete-menu">
                  <Button
                    key={`delete-btn-${hasHorizontalScroll}`}
                    icon="pi pi-trash"
                    className="calendarAssignments-massDelete-btn__roles p-button-rounded p-button-danger ml-2"
                    tooltip="Удаление"
                    onClick={(e) => deleteMenuRef.current?.toggle(e)}
                    disabled={!selectedDepartmentFilter}
                    tooltipOptions={{
                      position: hasHorizontalScroll ? 'left' : 'top'
                    }}
                  />
                  <Menu model={deleteMenuItems} popup ref={deleteMenuRef} />
                </React.Fragment>
              )
            ]
      }
      dropdownWidth="270px"
    />
  );
};

export default CalendarAssignmentsHeader;
